import { useContext, useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Context } from '../../../Context';
import { ApartmentViewContext } from '../context/ApartmentViewContext';
import {
	arrangeModeName,
	hotSpotModeName,
	infoModeName,
	insolationModeName,
	isometricModeName,
	stylesModeName,
} from '../context/contextStrings';
import { getRotateToNorthAngleDeg } from '../utils/geometryUtils';
import { ReactComponent as IconNorth } from '../assets/icons/icon-north.svg';
import { ReactComponent as IconSun } from '../assets/icons/icon-sun.svg';
import { ReactComponent as IconChair } from '../assets/icons/icon-armchair.svg';
import { ReactComponent as IconViewPoint } from '../assets/icons/icon-viewpoint.svg';
import { ReactComponent as IconIsometric } from '../assets/icons/icon-isometric.svg';
import { ReactComponent as IconHeart } from '../assets/icons/icon-heart.svg';
import { ReactComponent as IconDownload } from '../assets/icons/icon-download.svg';
import PdfModal from './PdfModal';
import ApartmentQueryButton from './ApartmentQueryButton';
import styles from '../../../styles/aprt.module.scss';

function PlanButtons({ apartmentData, dataUrl, projectSetup }) {
	const {
		activeBuilding,
		edificesData,
		favoriteApartmentUUIDs,
		toggleFavoriteApartmentHandler,
	} = useContext(Context);
	const {
		apartmentViewMode,
		setApartmentViewMode,
		doApartmentsHaveHotSpots,
		doApartmentsHaveVersions,
		doApartmentsHaveIsometricViews,
		setApartmentViewInteractions,
		setInfoTooltipData,
		isometricAngle,
	} = useContext(ApartmentViewContext);

	const rotationToNorthAngleDeg = getRotateToNorthAngleDeg(
		apartmentData.azimuth
	);
	const isRotatedToNorth = apartmentViewMode.rotatedToNorth;
	const selectedMode = apartmentViewMode.mode;
	const isItHotSpotMode = selectedMode === hotSpotModeName;
	const isItIsometricMode = selectedMode === isometricModeName;
	const iconNorthRef = useRef(null);

	const {
		render_top: isTopRenderEnabled,
		floorplan_insolation: isInsolationEnabled,
		render_360: isAxonometryEnabled,
		compartments_versioning: isVersioningEnabled,
		editing: isEditingEnabled,
		floorplan_pdf: isPdfSheetEnabled,
	} = projectSetup.apartments;
	const { render_ai: isHotSpotModeEnabled } = projectSetup.interiors;

	const checkCamerasForHotSpots = (apartmentData) => {
		if (!apartmentData || !doApartmentsHaveHotSpots) return false;

		const hasAIViewCamera = apartmentData.tiers.some((tier) =>
			tier.cameras.some((camera) => camera.name.includes('id'))
		); //TODO: add to context stings
		//TODO: this is temporary solution, should be changed to check if camera name string starts with determined string

		if (hasAIViewCamera) return true;

		return false;
	};
	const checkCamerasIsometricView = (apartmentData) => {
		if (!apartmentData || !doApartmentsHaveIsometricViews) return false;

		const hasIsometricViewCamera = apartmentData.tiers.some((tier) =>
			tier.cameras.some((camera) => camera.name.includes('axonometric'))
		); //TODO: add to context stings

		if (hasIsometricViewCamera) return true;

		return false;
	};
	const checkCompartmentsForVersions = (apartmentData) => {
		if (!apartmentData || !doApartmentsHaveVersions) return false;

		const versions = apartmentData.compartments.map(
			(compartments) => +compartments.versions
		);

		if (versions.some((number) => number > 1)) return true;

		return false;
	};

	const [areHotSpotsPresent, setAreHotSpotsPresent] = useState(false);
	const [areVersionsPresent, setAreVersionsPresent] = useState(false);
	const [isIsometricViewPresent, setIsIsometricViewPresent] = useState(false);

	useEffect(() => {
		if (!apartmentData) return;

		setAreHotSpotsPresent(checkCamerasForHotSpots(apartmentData));
		setAreVersionsPresent(checkCompartmentsForVersions(apartmentData));
		setIsIsometricViewPresent(checkCamerasIsometricView(apartmentData));
	}, [apartmentData]);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState('');

	const currentUuid = apartmentData.uuid;

	const isCurrentUuidInFavorites = favoriteApartmentUUIDs.includes(currentUuid);

	const handleRotate = () => {
		setApartmentViewMode(({ rotatedToNorth, ...rest }) => ({
			...rest,
			rotatedToNorth: !rotatedToNorth,
		}));
	};

	const handleViewModeChange = (value) => {
		setApartmentViewInteractions((prev) => ({
			...prev,
			selectedCompartmentInd: null,
		}));
		setApartmentViewMode(({ mode, rotatedToNorth, ...rest }) => ({
			...rest,
			mode: value,
			rotatedToNorth: false,
		}));
	};

	const handleClickOnDownload = async (uuid) => {
		//TODO: for now pdf files are named with adress_part, and kept in /apartments dir hence this temporary solution:
		//TODO: get the path from context / prop
		const basePath = `${dataUrl}edifices/${activeBuilding}/apartments`; //TODO: basePath and edifice number from context
		const apartmentAddressPart = apartmentData.id_original;
		const splitAddressPart = apartmentAddressPart.split('/');
		const fileName = splitAddressPart.join('-');
		// const fileName = apartmentData.id_original;
		const projectName = edificesData[0].project;

		const url = `${basePath}/${fileName}.pdf`;
		const response = await fetch(url);
		const blob = await response.blob();
		const link = document.createElement('a');
		link.href = window.URL.createObjectURL(blob);
		link.download = `${projectName}_${fileName}.pdf`;
		link.click();

		setPdfUrl(url);
		setIsModalOpen(true);
	};

	const updateTooltipOnHover = (str, e) => {
		if (e) {
			const rect = e.target.getBoundingClientRect();
			const position = {
				top: rect.top,
				bottom: rect.bottom,
				left: rect.left,
				right: rect.right,
				width: rect.width,
				height: rect.height,
			};

			str === null
				? setInfoTooltipData({ type: '', hovered: false, position: null })
				: setInfoTooltipData({ type: str, hovered: true, position });
		}
	};

	const getIconNorthTransform = () => {
		if (!isItIsometricMode && !isRotatedToNorth)
			return `rotate(${-rotationToNorthAngleDeg}deg)`;
		if (isItIsometricMode && !isRotatedToNorth)
			return `rotate(${isometricAngle}deg)`;
		return 'rotate(0deg)';
	};

	useEffect(() => {
		const targetAngle = (() => {
			if (!isItIsometricMode && !isRotatedToNorth)
				return -rotationToNorthAngleDeg;
			if (isItIsometricMode && !isRotatedToNorth) return isometricAngle;
			return 0;
		})();

		gsap.to(iconNorthRef.current, {
			// shortRotation: targetAngle, // warning na konsoli `Invalid property shortRotation set to 0 Missing plugin? gsap.registerPlugin()`
			rotation: targetAngle + '_short',
			duration: 0.5,
			ease: 'power1.inOut',
			transformOrigin: 'center',
		});
	}, [
		isRotatedToNorth,
		isItIsometricMode,
		rotationToNorthAngleDeg,
		isometricAngle,
	]);

	return (
		<div className={styles.planButtons__wrapper}>
			<div className={styles.planButtons__block}>
				<div className={styles.planButtons__container_first}>
					<button
						className={`${styles.planButton} ${
							isRotatedToNorth ? styles.planButton__selected : ''
						}`}
						onClick={() => handleRotate()}
						onMouseEnter={(e) => updateTooltipOnHover('rotateToNorth', e)}
						onMouseLeave={(e) => updateTooltipOnHover(null, e)}
						disabled={isItHotSpotMode}
					>
						<IconNorth
							ref={iconNorthRef}
							title="obróć na północ" //TODO: add to locales
							className={styles.planButton__icon}
						/>
					</button>
					{isTopRenderEnabled && (
						<button
							title="rzut 3D"
							className={`${styles.planButton} ${
								selectedMode === stylesModeName
									? styles.planButton__selected
									: ''
							}`}
							onClick={() => handleViewModeChange(stylesModeName)}
							onMouseEnter={(e) => updateTooltipOnHover(stylesModeName, e)}
							onMouseLeave={(e) => updateTooltipOnHover(null, e)}
							//   disabled={selectedHotSpot}
						>
							3D
						</button>
					)}
					<button
						title="tryb informacyjny"
						className={`${styles.planButton} ${
							selectedMode === infoModeName ? styles.planButton__selected : ''
						}`}
						onClick={() => handleViewModeChange(infoModeName)}
						onMouseEnter={(e) => updateTooltipOnHover(infoModeName, e)}
						onMouseLeave={(e) => updateTooltipOnHover(null, e)}
						//   disabled={selectedHotSpot}
					>
						m<sup>2</sup>
					</button>
					{isInsolationEnabled && (
						<button
							className={`${styles.planButton} ${
								selectedMode === insolationModeName
									? styles.planButton__selected
									: ''
							}`}
							onClick={() => handleViewModeChange(insolationModeName)}
							onMouseEnter={(e) => updateTooltipOnHover(insolationModeName, e)}
							onMouseLeave={(e) => updateTooltipOnHover(null, e)}
							// disabled={selectedHotSpot}
						>
							<IconSun
								title="tryb nasłonecznienia"
								className={styles.planButton__icon}
							/>
						</button>
					)}
					{isVersioningEnabled && doApartmentsHaveVersions && (
						<button
							className={`${styles.planButton} ${
								selectedMode === arrangeModeName
									? styles.planButton__selected
									: ''
							}`}
							onClick={() => handleViewModeChange(arrangeModeName)}
							onMouseEnter={(e) => updateTooltipOnHover(arrangeModeName, e)}
							onMouseLeave={(e) => updateTooltipOnHover(null, e)}
							disabled={!areVersionsPresent}
						>
							<IconChair
								title="tryb aranżacji"
								className={styles.planButton__icon}
							/>
						</button>
					)}
					{isAxonometryEnabled && doApartmentsHaveIsometricViews && (
						<button
							className={`${styles.planButton} ${
								selectedMode === isometricModeName
									? styles.planButton__selected
									: ''
							}`}
							onClick={() => handleViewModeChange(isometricModeName)}
							onMouseEnter={(e) => updateTooltipOnHover(isometricModeName, e)}
							onMouseLeave={(e) => updateTooltipOnHover(null, e)}
							disabled={!isIsometricViewPresent}
						>
							<IconIsometric
								title="tryb aksonometryczny"
								className={styles.planButton__icon}
								style={{ transform: `rotate(180deg)` }}
							/>
						</button>
					)}
					{isHotSpotModeEnabled && doApartmentsHaveHotSpots && (
						<button
							className={`${styles.planButton} ${
								selectedMode === hotSpotModeName
									? styles.planButton__selected
									: ''
							}`}
							onClick={() => handleViewModeChange(hotSpotModeName)}
							onMouseEnter={(e) => updateTooltipOnHover(hotSpotModeName, e)}
							onMouseLeave={(e) => updateTooltipOnHover(null, e)}
							disabled={!areHotSpotsPresent}
						>
							<IconViewPoint
								title="tryb widoków wnętrz ai"
								className={styles.planButton__icon}
							/>
						</button>
					)}
				</div>
				<div className={styles.planButtons__container_second}>
					<button
						className={`${styles.planButton} ${
							isCurrentUuidInFavorites ? styles.planButton__selected : ''
						}`}
						onClick={() => toggleFavoriteApartmentHandler(currentUuid)}
					>
						<IconHeart
							title={
								isCurrentUuidInFavorites
									? 'usuń z ulubionych'
									: 'dodaj do ulubionych'
							} //TODO: add to locales
							className={styles.planButton__icon}
						/>
					</button>
					{isPdfSheetEnabled && (
						<button
							className={styles.planButton}
							onClick={() => handleClickOnDownload(currentUuid)}
						>
							<IconDownload
								title="pobierz kartę katalogową" //TODO: add to locales
								className={styles.planButton__icon}
							/>
						</button>
					)}
				</div>
				{isModalOpen && (
					<PdfModal
						isOpen={isModalOpen}
						pdfUrl={pdfUrl}
						onClose={() => setIsModalOpen(false)}
					/>
				)}
			</div>
			<ApartmentQueryButton
				apartmentData={apartmentData}
				isBackendWeblo={projectSetup.website.is_backend_weblo}
			/>
		</div>
	);
}

export default PlanButtons;
